import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, type TicketDetailResponse } from '../../lib/api';
import { cacheFirst } from '../../lib/cacheFirst';
import { cacheTicketDetail, getCachedTicketDetail } from '../../lib/db';
import { effectiveResolveDeadline } from '@nbh/shared';
import { useNow } from '../../hooks/useNow';
import { useRealtime } from '../../hooks/useRealtime';
import { BreachBadge, breachBadgesFor } from '../ui/BreachBadge';
import { PriorityBadge } from '../ui/PriorityBadge';
import { StatusBadge } from '../ui/StatusBadge';
import { InteractiveChecklist } from './InteractiveChecklist';
import { TicketActionToolbar } from './TicketActionToolbar';
import { TicketAuditTrail } from './TicketAuditTrail';
import { TicketChat } from './TicketChat';
import { TicketIssuePhoto } from './TicketIssuePhoto';
import { TicketPhotoComparison } from './TicketPhotoComparison';
import { TicketPhotoGallery } from './TicketPhotoGallery';
import { formatDateTime, formatSlaSpan } from '../../lib/format';

type Tab = 'details' | 'photos' | 'checklist' | 'audit' | 'chat';

/**
 * The tabs a job always has. Photos is not among them.
 *
 * A job with no media gets no photos tab at all — not an empty one, not a disabled one.
 * Photos are optional at every stage (D2, D33), so their absence is the ordinary case,
 * and an empty tab would turn the ordinary case into a visible gap someone feels they
 * ought to fill.
 */
const TABS: Tab[] = ['details', 'checklist', 'audit', 'chat'];

function tabsFor(ticket: TicketDetailResponse): Tab[] {
  if (ticket.attachments.length === 0) return TABS;

  const [details, ...rest] = TABS;
  return [details, 'photos', ...rest];
}

export interface TicketDetailProps {
  ticketId: string;
  /** Vendors cannot post internal notes, so the toggle is hidden for them. */
  canPostInternal?: boolean;
}

/**
 * One job, in full.
 *
 * Reads the cache before the network (Rule 4): the instructions, the checklist and the
 * SLA deadline are exactly what a technician needs while standing at the fault, which is
 * where the signal is worst. The action buttons still call the server and still show their
 * own failure, so nothing here can look like it saved when it did not.
 */
export function TicketDetail({ ticketId, canPostInternal = true }: TicketDetailProps) {
  // The screen's badge clock: one 30-second interval for the header badge and the
  // deadline rows together, cleared on unmount.
  const now = useNow(30_000);
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('details');
  const [ticket, setTicket] = useState<TicketDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const cancelRef = useRef<(() => void) | null>(null);

  const refresh = useCallback(() => {
    cancelRef.current?.();
    cancelRef.current = cacheFirst<TicketDetailResponse>({
      readCache: () => getCachedTicketDetail(ticketId),
      load: () => api.getTicket(ticketId),
      writeCache: cacheTicketDetail,
      onValue: (fetched) => {
        setTicket(fetched);
        // A cached answer clears a failure from an earlier attempt: the screen has content
        // again, and showing the error instead would hide it.
        setError(null);
      },
      onError: (caught) => setError(caught.message),
    });
  }, [ticketId]);

  useEffect(() => {
    refresh();
    return () => cancelRef.current?.();
  }, [refresh]);

  // One refetch path for everything: our own actions, another technician's tick, and
  // the escalation sweep all land here, so the view can never drift from the server.
  useRealtime(
    {
      onTicketUpdated: (payload) => {
        if (payload.ticketId === ticketId) refresh();
      },
    },
    ticketId,
  );

  if (error)
    return (
      <p role="alert" className="p-4 text-sm text-red-400">
        {error}
      </p>
    );
  if (!ticket) return <p className="p-4 text-sm text-slate-400">{t('detail.loading')}</p>;

  return (
    <div className="flex min-h-dvh flex-col">
      <DetailHeader ticket={ticket} now={now} />
      <TabBar tabs={tabsFor(ticket)} active={tab} onSelect={setTab} />

      {tab === 'chat' ? (
        // The chat tab owns the pinned slot (D53). Three things competed for the bottom of
        // a 380px screen here — the tab bar, the action toolbar and the composer — and the
        // composer lost, sitting in the page flow below the fold. On this tab the composer
        // takes the toolbar's place; a technician who wants to resolve switches to Details,
        // one tap, which is cheaper than a permanently cramped screen. Do not "fix" the
        // missing toolbar on this tab: the overlap is what it would bring back.
        <TicketChat
          ticketId={ticketId}
          canPostInternal={canPostInternal}
          events={ticket.auditLog}
        />
      ) : (
        <>
          <div className="flex-1 p-4">
            {tab === 'details' && <DetailsTab ticket={ticket} now={now} />}
            {tab === 'photos' && <TicketPhotoGallery attachments={ticket.attachments} />}
            {tab === 'checklist' && (
              <InteractiveChecklist
                ticketId={ticketId}
                status={ticket.status}
                steps={ticket.checklistItems}
                onChanged={refresh}
              />
            )}
            {tab === 'audit' && <TicketAuditTrail entries={ticket.auditLog} />}
          </div>

          {/*
            Pinned above the tab bar, not to the viewport bottom. `sticky bottom-0` put this
            squarely underneath the fixed bar: the buttons rendered, sat inside the viewport,
            and could not be tapped — `elementFromPoint` over Claim job returned a tab-bar
            icon. Offset by the bar's own height so the two cannot drift apart.
          */}
          <div className="sticky bottom-[var(--nav-h)]">
            <TicketActionToolbar ticket={ticket} onChanged={refresh} />
          </div>
        </>
      )}
    </div>
  );
}

function DetailHeader({ ticket, now }: { ticket: TicketDetailResponse; now: Date }) {
  const { t } = useTranslation();
  const breaches = breachBadgesFor(ticket, now);

  return (
    <header className="border-b border-slate-800 p-4">
      <h1 className="text-lg font-semibold">{ticket.title}</h1>
      <p className="text-xs text-slate-500">
        #{ticket.reference} · {ticket.locationName ?? t('queue.noLocation')}
      </p>
      {breaches.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {breaches.map((state) => (
            <BreachBadge key={state.kind} state={state} />
          ))}
        </div>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <PriorityBadge priority={ticket.priority} />
        <StatusBadge status={ticket.status} holdReason={ticket.holdReason} />
      </div>

      {/*
        Both are conditional on their own photos and on the job's state, and the two
        states never overlap: work still to do gets the fault (R4), work waiting on a
        verifier gets before-and-after (R6). Where the photos do not exist, both render
        literally nothing and the header is the header it has always been.
      */}
      <TicketIssuePhoto attachments={ticket.attachments} status={ticket.status} />
      <TicketPhotoComparison attachments={ticket.attachments} status={ticket.status} />

      <LifecycleAttribution ticket={ticket} />
    </header>
  );
}

/**
 * Who took the job on, who finished it, who checked it, who closed it.
 *
 * One line per stage that has actually happened — a null timestamp means it has not, and
 * an empty row would only be noise on a phone. Name and timestamp are independently
 * nullable on the wire: a hard-deleted account (D25) nulls the ticket's reference to the
 * person while the timestamp stays, so a stage with a time and no name is a real state
 * and reads as "a former staff member" rather than silently disappearing.
 */
function LifecycleAttribution({ ticket }: { ticket: TicketDetailResponse }) {
  const { t } = useTranslation();

  const stages: Array<{ labelKey: string; name: string | null; at: string | null }> = [
    { labelKey: 'detail.claimedBy', name: ticket.claimedByName, at: ticket.claimedAt },
    { labelKey: 'detail.completedBy', name: ticket.completedByName, at: ticket.completedAt },
    { labelKey: 'detail.verifiedBy', name: ticket.verifiedByName, at: ticket.verifiedAt },
    { labelKey: 'detail.closedBy', name: ticket.closedByName, at: ticket.closedAt },
  ];

  const happened = stages.filter((stage) => stage.at !== null);
  if (happened.length === 0) return null;

  return (
    <dl className="mt-3 space-y-1">
      {happened.map((stage) => (
        <div key={stage.labelKey} className="flex flex-wrap gap-x-2 text-xs">
          <dt className="text-slate-500">{t(stage.labelKey)}</dt>
          <dd className="text-slate-300">
            {stage.name ?? t('detail.formerStaff')} · {formatDateTime(stage.at)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function TabBar({
  tabs,
  active,
  onSelect,
}: {
  tabs: Tab[];
  active: Tab;
  onSelect: (tab: Tab) => void;
}) {
  const { t } = useTranslation();

  return (
    <nav className="flex overflow-x-auto border-b border-slate-800" aria-label={t('detail.tabs')}>
      {tabs.map((name) => (
        <button
          key={name}
          type="button"
          role="tab"
          aria-selected={active === name}
          onClick={() => onSelect(name)}
          className={`shrink-0 border-b-2 bg-transparent px-4 py-3 text-sm font-medium ${
            active === name
              ? 'border-brookfields-gold text-slate-100'
              : 'border-transparent text-slate-400'
          }`}
        >
          {t(`detail.tab.${name}`)}
        </button>
      ))}
    </nav>
  );
}

function DetailsTab({ ticket, now }: { ticket: TicketDetailResponse; now: Date }) {
  const { t } = useTranslation();

  // Live deadlines render through the badge formatter — "in 1h 22m", "14m overdue" —
  // and keep ticking; a settled stage shows the deadline it was measured against,
  // as a static record (the header badge already says whether it was met late).
  const deadlinePhrase = (deadline: Date, rawIso: string, settledAt: string | null): string => {
    if (settledAt !== null) return formatDateTime(rawIso);
    return now.getTime() > deadline.getTime()
      ? t('detail.overdueBy', { span: formatSlaSpan(deadline, now, 'overdue') })
      : t('detail.dueIn', { span: formatSlaSpan(deadline, now, 'remaining') });
  };

  // The same hold correction every consumer applies (D6): an open hold credits the
  // resolve deadline minute for minute, so a held job never counts down or overdue
  // on a vendor's time. The claim deadline takes no such credit — holds start after
  // acknowledgement by construction.
  const resolveDeadline = effectiveResolveDeadline(
    {
      slaResolveDeadline: new Date(ticket.slaResolveDeadline),
      currentHoldStartedAt: ticket.currentHoldStartedAt
        ? new Date(ticket.currentHoldStartedAt)
        : null,
    },
    now,
  );

  return (
    <dl className="space-y-3 text-sm">
      <div>
        <dt className="text-slate-400">{t('detail.description')}</dt>
        <dd className="text-slate-100">{ticket.description || t('detail.noDescription')}</dd>
      </div>
      <div>
        <dt className="text-slate-400">{t('detail.assignee')}</dt>
        <dd className="text-slate-100">{ticket.assigneeName ?? t('detail.unassigned')}</dd>
      </div>
      <div>
        <dt className="text-slate-400">{t('detail.claimBy')}</dt>
        <dd className="text-slate-100">
          {deadlinePhrase(new Date(ticket.slaAckDeadline), ticket.slaAckDeadline, ticket.acknowledgedAt)}
        </dd>
      </div>
      <div>
        <dt className="text-slate-400">{t('detail.resolveBy')}</dt>
        <dd className="text-slate-100">
          {deadlinePhrase(resolveDeadline, ticket.slaResolveDeadline, ticket.resolvedAt)}
        </dd>
      </div>
      {ticket.totalHoldDurationMins > 0 && (
        <div>
          <dt className="text-slate-400">{t('detail.heldFor')}</dt>
          <dd className="text-slate-100">
            {t('detail.heldMinutes', { count: ticket.totalHoldDurationMins })}
          </dd>
        </div>
      )}
    </dl>
  );
}
